import { useState } from "react";
import { LoadingSpinner } from "@/core/components/loading-spinner";
import { Button } from "@/core/components/ui/button";
import { Stack } from "@/core/components/ui/stack";
import { Typography } from "@/core/components/ui/typography";
import { cn } from "@/core/utils/utils";
import {
  STATEMENT_IDENTITY,
  useGetAiScriptStatement,
} from "@/payroll/ai-script/query";
import {
  referenceKey,
  StatementRenderer,
  type StatementItemReference,
} from "@/payroll/ai-script/statement/statement-renderer";
import { STATEMENT_RELEASE_REASON } from "@/payroll/ai-script/types";
import { PackChatState } from "@/payroll/pack-chat/pack-chat-state";
import { useGetPackThreads } from "@/payroll/pack-chat/query";
import { PACK_CHAT_STATUS } from "@/payroll/pack-chat/types";
import { usePaypacksAccess } from "@/payroll/pay-breakdown/payroll-emails-access";
import { PayPeriodLockedMark } from "@/payroll/pay-periods/pay-period-locked";
import { AiScriptTechnicianFeedbackForm } from "./ai-script-technician-feedback-form";
import { PackQuestionHistory } from "./pack-question-history";
import { PayApprovalPanel } from "./pay-approval-panel";

interface TechnicianStatementViewProps {
  businessId: string | null;
  runId: string;
  isLocked: boolean;
}

const PACK_TAB = {
  pay: "pay",
  questions: "questions",
} as const;

type PackTab = (typeof PACK_TAB)[keyof typeof PACK_TAB];

const TAB_LABELS: Record<PackTab, string> = {
  [PACK_TAB.pay]: "My pay",
  [PACK_TAB.questions]: "Questions",
};

/**
 * Two tabs over one pack: the pay, and the questions asked about it.
 *
 * The questions used to live under the statement, which on a real pack is
 * several screens down — so a technician with a reply waiting had to scroll the
 * whole length of their own pay to find it, and had no way of knowing it was
 * there. A tab says it in the one place they always see, and going back to the
 * figures is one tap either way.
 */
const PackTabs = ({
  value,
  onChange,
  openCount,
}: {
  value: PackTab;
  onChange: (next: PackTab) => void;
  openCount: number;
}) => (
  <Stack
    direction="row"
    align="center"
    gap="none"
    fullWidth
    role="tablist"
    aria-label="Your pack"
    className="rounded-lg bg-surface-secondary p-0.5"
  >
    {Object.values(PACK_TAB).map((tab) => (
      <button
        key={tab}
        type="button"
        role="tab"
        aria-selected={value === tab}
        onClick={() => onChange(tab)}
        className={cn(
          "flex flex-1 items-center justify-center gap-1.5 rounded-md px-3 py-2 text-sm font-medium transition-colors",
          {
            "bg-surface-primary text-primary-text shadow-sm": value === tab,
            "text-secondary-text hover:text-primary-text": value !== tab,
          },
        )}
      >
        {TAB_LABELS[tab]}
        {/* A dot, not a count badge: the only thing worth interrupting someone's
            pay for is that something of theirs is still open. */}
        {tab === PACK_TAB.questions && openCount > 0 && (
          <>
            <span
              className="block size-[7px] rounded-full bg-gold-60"
              aria-hidden
            />
            <span className="sr-only">
              {openCount} open {openCount === 1 ? "question" : "questions"}
            </span>
          </>
        )}
      </button>
    ))}
  </Stack>
);

// The technician-facing surface. It reads ONLY the per-technician statement
// endpoint (never the raw workbook), so a technician on a shared department
// sheet can't see anyone else's rows.
export const TechnicianStatementView = ({
  businessId,
  runId,
  isLocked,
}: TechnicianStatementViewProps) => {
  const { data, isLoading, error } = useGetAiScriptStatement(
    businessId,
    runId,
    { identity: STATEMENT_IDENTITY.viewer, enabled: true },
  );
  // Held here rather than in either child: the statement raises the figure and the
  // form consumes it.
  const [attached, setAttached] = useState<StatementItemReference | null>(null);
  const [sentThreadId, setSentThreadId] = useState<string | null>(null);
  const [tab, setTab] = useState<PackTab>(PACK_TAB.pay);
  // The same gate the manager side reads, because the server enforces it on both:
  // a business without saved PayPacks access has no PackChat, and its endpoints 404.
  //
  // Without this a technician at such a business was invited to ask about every
  // figure on their pay and told "PackChat couldn't be loaded. Contact support" when
  // they opened the tab — which is the worst thing a payroll screen can say to
  // somebody about their own money, for a feature nobody had turned on.
  const { enabled: packChatEnabled } = usePaypacksAccess();

  // Questions belong to the run the FIGURES came from. After a rerun the endpoint
  // serves the run their pay was sent from, so asking about the requested run would
  // file a question under one run while this tab reads another — and the question a
  // technician just sent would vanish. Falls back to the requested run for an api too
  // old to report it, which is what such an api served anyway.
  const statementRunId = data?.statement_run_id ?? runId;

  // Withheld pay has no threads to read, and the server refuses them for exactly the
  // reason it refuses the statement — so asking anyway is a 404 on every open. Waits
  // for the statement to come back released before it asks at all.
  const packChatReady = packChatEnabled && !!data && data.released !== false;

  // Fetched here rather than inside the questions tab: the tab itself has to say
  // whether anything is open, and it says that whichever tab is showing.
  const threadsQuery = useGetPackThreads(
    businessId,
    statementRunId,
    packChatReady,
  );
  // Read these during render so the tab handler receives changes in freshness.
  const { isStale: threadsAreStale, isFetching: threadsAreFetching } =
    threadsQuery;
  const threads = threadsQuery.data?.threads ?? [];
  const openCount = threads.filter(
    (thread) => thread.status === PACK_CHAT_STATUS.open,
  ).length;

  const handleTabChange = (next: PackTab) => {
    if (next === tab) return;
    setTab(next);
    // The badge keeps this query mounted while My pay is open.
    if (next === PACK_TAB.questions && threadsAreStale && !threadsAreFetching) {
      void threadsQuery.refetch();
    }
  };

  if (isLoading) {
    return (
      <Stack fullWidth align="center" justify="center" className="h-full">
        <LoadingSpinner />
      </Stack>
    );
  }

  const centered = (title: string, body: string) => (
    <Stack
      fullWidth
      align="center"
      justify="center"
      gap="xs"
      className="h-full px-6 text-center"
    >
      <Typography variant="subtitleW2">{title}</Typography>
      <Typography variant="bodyW3" textColor="secondary">
        {body}
      </Typography>
    </Stack>
  );

  if (error) {
    return centered(
      "Statement unavailable",
      "We couldn't load your statement. Please try again shortly.",
    );
  }

  // The requester is a technician we couldn't link to their ServiceTitan record.
  if (data?.linked === false) {
    return centered(
      "Your account isn't linked yet",
      "We couldn't match your login to your technician record. Please contact your admin so they can link it.",
    );
  }

  // Payroll has run but this technician's pay is not theirs to see yet, so the
  // figures are still liable to move. Said plainly and BEFORE the empty check:
  // routed there instead, a technician reads "No pay this period" for pay that
  // exists — the alarming version of the same wait. `=== false` on purpose, so an
  // api that predates the gate reads as released rather than withholding everything.
  if (data?.released === false) {
    // Which wait it is matters. Told to expect a send, a technician at a business
    // that never emails PayPacks waits on nothing and chases an email nobody sent;
    // told the period is still running when it closed last week, they distrust the
    // screen.
    return data.release_reason === STATEMENT_RELEASE_REASON.periodOpen
      ? centered(
          "This pay period isn't finished yet",
          "Your statement will appear here once the pay period ends and your pay is worked out.",
        )
      : centered(
          "Your pay isn't ready yet",
          "Your manager is still finalizing payroll for this period. Your statement will appear here as soon as it's published.",
        );
  }

  const statement = data?.statement;
  const isEmpty = !statement || (statement.blocks?.length ?? 0) === 0;
  if (isEmpty) {
    return centered(
      "No pay this period",
      "There's nothing on your statement for this pay period yet.",
    );
  }

  const approvalPanel = (
    <PayApprovalPanel
      businessId={businessId}
      runId={statementRunId}
      statement={data}
      isLocked={isLocked}
    />
  );

  // Without PayPacks, this is a pay statement and nothing else: no tabs to switch,
  // no flag beside each figure, no box to send a question nobody would receive — and
  // no sign-off, which rides on the same saved selection. Approving emails the office, so a
  // business that has been told nothing will send does not get a button that sends
  // something.
  if (!packChatEnabled) {
    return (
      <Stack gap="none" align="stretch" fullWidth className="h-full min-h-0">
        <Stack gap="none" align="stretch" className="min-h-0 flex-1">
          <StatementRenderer statement={statement} />
        </Stack>
      </Stack>
    );
  }

  return (
    <Stack gap="none" align="stretch" fullWidth className="h-full min-h-0">
      <Stack gap="none" fullWidth className="shrink-0">
        <Stack
          gap="none"
          fullWidth
          className="mx-auto max-w-2xl px-4 pb-3 pt-4"
        >
          <PackTabs
            value={tab}
            onChange={handleTabChange}
            openCount={openCount}
          />
        </Stack>
      </Stack>

      <Stack gap="none" align="stretch" className="min-h-0 flex-1">
        {tab === PACK_TAB.pay ? (
          <StatementRenderer
            statement={statement}
            itemAction={
              isLocked
                ? undefined
                : {
                    onAction: setAttached,
                    activeKey: attached ? referenceKey(attached) : null,
                  }
            }
            // Agreeing comes first and asking second: a technician who has read
            // to the bottom of their own pay is usually done, and the question
            // box is for the times they are not.
            //
            // The wrapper this sits in is align="stretch" for a reason — see
            // technician-workbook-experience. Without it the pack is content-sized,
            // so swapping the full-width approve button for a line of text narrowed
            // the whole statement by ~150px under the technician's finger.
            footer={
              <>
                {approvalPanel}
                {isLocked ? (
                  <Stack
                    direction="row"
                    align="center"
                    gap="sm"
                    className="border-t border-stroke-tertiary pt-5"
                  >
                    <PayPeriodLockedMark />
                    <Typography variant="bodyW3" textColor="secondary">
                      This pay period is locked. You can still read and reply to
                      existing questions.
                    </Typography>
                  </Stack>
                ) : !threadsQuery.data ? (
                  <PackChatState
                    compact
                    loading={!threadsQuery.error}
                    error={threadsQuery.error}
                    title="Questions couldn't be loaded"
                    action={
                      threadsQuery.error ? (
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => void threadsQuery.refetch()}
                        >
                          Try again
                        </Button>
                      ) : undefined
                    }
                  />
                ) : (
                  <AiScriptTechnicianFeedbackForm
                    businessId={businessId}
                    runId={statementRunId}
                    aiEnabled={threadsQuery.data?.ai_enabled ?? false}
                    attached={attached}
                    onClearAttached={() => setAttached(null)}
                    // Sending moves them to the conversation they just started,
                    // so the reply lands somewhere they have already been.
                    onSent={(threadId) => {
                      setSentThreadId(threadId);
                      setTab(PACK_TAB.questions);
                    }}
                  />
                )}
              </>
            }
          />
        ) : (
          <Stack
            gap="none"
            fullWidth
            data-pack-question-scroll
            className="quiet-scrollbar h-full min-h-0 overflow-y-auto pb-6"
          >
            <Stack gap="none" fullWidth className="mx-auto max-w-2xl px-4 py-5">
              <PackQuestionHistory
                businessId={businessId}
                runId={statementRunId}
                threads={threads}
                sentThreadId={sentThreadId}
                loading={threadsQuery.isLoading}
                error={threadsQuery.error}
                onRetry={() => void threadsQuery.refetch()}
              />
            </Stack>
          </Stack>
        )}
      </Stack>
    </Stack>
  );
};
